import { differenceInDays } from 'date-fns';
import { injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import {
    EtfBreakdownCategory,
    EtfBreakdownDuration,
    EtfFilterInverseOptions,
    EtfScreenerSortParams,
    GlobalGroupCode,
    IndustryType,
    IpoDataSortOption,
    IpoPerformanceRange,
} from '../constants/lookups';
import { Quote } from '../types';
import {
    Earnings,
    EarningsWithExtraInfo,
    EtfBreakdownEntry,
    EtfBreakdownWithExtraInfo,
    EtfDetails,
    EtfFilterOption,
    EtfInit,
    EtfInitCommonParams,
    EtfSymbolEntryWithExtraInfo,
    Ipo,
    IpoWithExtraInfo,
    OptionsRadarBreakdown,
    QuoteProfile,
    QuoteProfileWithExtraInfo,
    SectorBreakDown,
    SectorRanking,
    TopTenBreakdown,
} from '../types/Research.type';
import {
    formatEtfBySector,
    formatEtfInitCommonParams,
    formatEtfSymbolEntryWithExtraInfo,
    formatIpo,
    formatQuoteProfile,
    formatUpcomingEarnings,
    getSectorIndustryByIndustryGroupCode,
} from '../utils';
import { IResearchApi } from './interfaces/IResearchApi';

@injectable()
export class ResearchApi extends CachedResponseApi implements IResearchApi {
    async getQuoteProfile(symbol: string): Promise<QuoteProfileWithExtraInfo> {
        const response = await this.sdks.orbis.get<QuoteProfile>('research', undefined, { urlQuery: symbol });
        return formatQuoteProfile(response.data);
    }

    async getEtfDetails(symbol: string): Promise<EtfDetails> {
        const response = await this.sdks.orbis.get('research/etf/details', undefined, { params: { symbol } });
        return response.data;
    }

    async getSectorsRanking(industryType: IndustryType): Promise<SectorRanking[]> {
        const response = await this.sdks.orbis.get('research/heatMap/industryRanking', undefined, {
            params: {
                industryType,
            },
        });
        return response.data;
    }

    async getSectorBreakdown(): Promise<SectorBreakDown> {
        const response = await this.sdks.orbis.get('research/etf/breakdown', undefined, {
            params: {
                duration: EtfBreakdownDuration.OneDay,
                displaySize: 10,
                exposure: 70,
            },
        });
        return response.data;
    }

    async getTopTenEtfsBySector(industryGroupCode: GlobalGroupCode): Promise<EtfBreakdownWithExtraInfo[]> {
        const data = await this.getSectorBreakdown();
        return formatEtfBySector(data, industryGroupCode);
    }

    async getTopTenCompanies(industryGroupCode: GlobalGroupCode): Promise<Quote[]> {
        const response = await this.sdks.orbis.get<Quote[]>('research/heatMap/symbols', undefined, {
            params: {
                industryCode: industryGroupCode,
                top: true,
                numSymbols: 10,
                amountLimit: 1,
                exchangeFilter: 1,
            },
        });
        return response.data;
    }

    async getSectorIndustries(industryGroupCode: GlobalGroupCode): Promise<SectorRanking[]> {
        const data = await this.getSectorsRanking(IndustryType.Industry);
        return getSectorIndustryByIndustryGroupCode(data, industryGroupCode);
    }

    async getUpcomingEarnings(symbols?: string[], days?: number): Promise<EarningsWithExtraInfo[]> {
        const response = await this.sdks.orbis.get<Earnings[]>('research/earnings', undefined, {
            params: {
                excludeOtcs: false,
                days: days || 1,
                loadQuotes: true,
                symbols: symbols?.join(','),
            },
        });

        return response.data?.map(formatUpcomingEarnings);
    }

    async getIpoUpcoming(): Promise<IpoWithExtraInfo[]> {
        const response = await this.sdks.orbis.get<Ipo[]>('research/ipo/upcoming');

        return response?.data.map(formatIpo);
    }

    async getIpoRecent(sort?: IpoDataSortOption, loadQuotes?: boolean): Promise<IpoWithExtraInfo[]> {
        const response = await this.sdks.orbis.get<Ipo[]>('research/ipo/recent', undefined, {
            params: {
                loadQuotes: loadQuotes || true,
                sort,
            },
        });

        return response?.data.map(formatIpo);
    }

    async getIpoPerformance(
        loadQuotes?: boolean,
        time?: IpoPerformanceRange,
        sort?: IpoDataSortOption,
    ): Promise<IpoWithExtraInfo[]> {
        const response = await this.sdks.orbis.get<Ipo[]>('research/ipo/performance', undefined, {
            params: {
                loadQuotes: loadQuotes || true,
                time,
                sort,
            },
        });

        return response?.data.map(formatIpo);
    }

    async getIpoDaysOnMarket(symbol: string): Promise<number> {
        const [performanceData, recentData] = await Promise.all([
            this.getIpoPerformance(false, IpoPerformanceRange.OneYear),
            this.getIpoRecent(),
        ]);

        const ipo = [...performanceData, ...recentData].find((item) => item.symbol === symbol);
        const ipoDaysOnMarket = ipo?.ipoDate ? differenceInDays(new Date(), new Date(ipo.ipoDate)) : 0;

        return ipoDaysOnMarket;
    }

    async getTopTenEtf(
        dataset: EtfBreakdownCategory,
        inverseType?: EtfFilterOption,
        leverageType?: EtfFilterOption,
    ): Promise<EtfSymbolEntryWithExtraInfo[]> {
        const response = await this.sdks.orbis.get<TopTenBreakdown>('research/etf/top10', undefined, {
            params: {
                inverseType: inverseType ? inverseType.value : EtfFilterInverseOptions.Any,
                leverageType: leverageType ? leverageType.value : EtfFilterInverseOptions.Any,
            },
        });

        return response?.data?.[dataset].map(formatEtfSymbolEntryWithExtraInfo);
    }

    async getEtfInitCommonParams(): Promise<EtfInitCommonParams> {
        const response = await this.sdks.orbis.get<EtfInit>('research/etf/init');
        return formatEtfInitCommonParams(response.data);
    }

    async getEtfScreener(
        inverseType: string,
        leverageType: string,
        assetId: string,
        categoryId: string,
        sponsorId: string,
        sort?: EtfScreenerSortParams,
        start?: number,
        num?: number,
        asc?: boolean,
        loadIndustries?: boolean,
        loadUpgrades?: boolean,
        loadEarnings?: boolean,
    ): Promise<EtfSymbolEntryWithExtraInfo[]> {
        const response = await this.sdks.orbis.get<EtfBreakdownEntry[]>('research/etf/screener', undefined, {
            params: {
                inverseType: inverseType ? inverseType : EtfFilterInverseOptions.Any,
                leverageType: leverageType ? leverageType : EtfFilterInverseOptions.Any,
                assetId: assetId ? assetId : undefined,
                categoryId: categoryId ? categoryId : undefined,
                sponsorId: sponsorId ? sponsorId : undefined,
                start: start ? start : undefined,
                num: num ? num : 5000,
                sort: sort ? sort : 'symbol',
                asc: asc ? asc : true,
                loadIndustries: loadIndustries ? loadIndustries : true,
                loadUpgrades: loadUpgrades ? loadUpgrades : true,
                loadEarnings: loadEarnings ? loadEarnings : true,
            },
        });

        return (response?.data.map(formatEtfSymbolEntryWithExtraInfo) || []).reverse();
    }

    async getOptionRadar(): Promise<OptionsRadarBreakdown> {
        const response = await this.sdks.orbis.get<OptionsRadarBreakdown>('research/option/radar');
        return response.data;
    }
}
